import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Import language files
import en from '../language/en.json';
import de from '../language/de.json';
import ae from '../language/ae.json';
import iq from '../language/iq.json';

const languages = {
  uk: en, // Changed from 'en' to 'uk' to match flag file
  us: en, // Use English for US
  ae,
  de,
  iq
};

type LanguageCode = 'uk' | 'us' | 'de' | 'ae' | 'iq';

interface LanguageContextType {
  currentLanguage: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: (key: string) => string;
  availableLanguages: { code: LanguageCode; name: string; flag: string }[];
  isInitialized: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const availableLanguages = [
  { code: 'uk' as LanguageCode, name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'us' as LanguageCode, name: 'United States', flag: '🇺🇸' },
  { code: 'ae' as LanguageCode, name: 'UAE', flag: '🇦🇪' },
  { code: 'de' as LanguageCode, name: 'Germany', flag: '🇩🇪' },
  { code: 'iq' as LanguageCode, name: 'Iraq', flag: '🇮🇶' }
];

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>('uk');
  const [isInitialized, setIsInitialized] = useState(false);

  // Load language from localStorage on mount
  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem('language') as LanguageCode;
      if (savedLanguage && languages[savedLanguage]) {
        setCurrentLanguage(savedLanguage);
      }
      
      // Keep LTR layout for all languages to prevent UI scattering
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = savedLanguage || 'uk';
    } catch (error) {
      console.warn('Failed to load language from localStorage:', error);
    } finally {
      setIsInitialized(true);
    }
  }, []);

  const setLanguage = (language: LanguageCode) => {
    setCurrentLanguage(language);
    try {
      localStorage.setItem('language', language);
    } catch (error) {
      console.warn('Failed to save language to localStorage:', error);
    }
    
    // Keep LTR layout for all languages to prevent UI scattering
    document.documentElement.dir = 'ltr';
    document.documentElement.lang = language;
  };

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = languages[currentLanguage];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if translation not found
        value = languages.uk;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return the key if translation not found
          }
        }
      }
    }
    
    return typeof value === 'string' ? value : key;
  };

  const value: LanguageContextType = {
    currentLanguage,
    setLanguage,
    t,
    availableLanguages,
    isInitialized
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
